import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
} from "@mantine/core";
import { useState, useRef } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  useErrorsAdmin,
  useCreateError,
  useUpdateError,
  useToggleErrorActive,
} from "../../../hooks/admin/useErrors";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import ErrorForm, {
  ErrorFormValues,
} from "../../../components/admin/errors/ErrorForm";
import { ErrorDefinition } from "../../../services/admin/errors.service";

export default function ErrorsAdminPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const [editingError, setEditingError] = useState<ErrorDefinition | null>(
    null
  );
  const formRef = useRef<HTMLFormElement>(null);

  const { data: errors = [], isLoading } = useErrorsAdmin();
  const createError = useCreateError();
  const updateError = useUpdateError();
  const toggleErrorActive = useToggleErrorActive();

  const handleAdd = () => {
    setEditingError(null);
    open();
  };

  const handleEdit = (item: ErrorDefinition) => {
    setEditingError(item);
    open();
  };

  const handleSubmit = async (values: ErrorFormValues) => {
    close();
    if (editingError) {
      await updateError.mutateAsync({
        id: editingError.id,
        ...values,
      });
    } else {
      await createError.mutateAsync(values);
    }
  };

  const handleToggleActive = async (item: ErrorDefinition) => {
    await toggleErrorActive.mutateAsync({
      id: item.id,
      isActive: !item.isActive,
    });
  };

  const columns: AdminColumn<ErrorDefinition>[] = [
    {
      key: "error",
      label: "Error",
      render: (item) => <Text fw={500}>{item.error}</Text>,
    },
    {
      key: "category",
      label: "Category",
      render: (item) => (
        <Badge
          variant="light"
          color={
            item.category === "accessioning"
              ? "blue"
              : item.category === "grossing"
              ? "grape"
              : "cyan"
          }
        >
          {item.category}
        </Badge>
      ),
    },
    {
      key: "description",
      label: "Description",
      render: (item) => (
        <Text size="sm" c="dimmed">
          {item.description || "-"}
        </Text>
      ),
    },
    {
      key: "flags",
      label: "Flags",
      render: (item) => (
        <Group gap="xs">
          {item.preventSignOff && (
            <Badge size="xs" variant="filled" color="orange">
              Prevent Sign-off
            </Badge>
          )}
          {item.blockCase && (
            <Badge size="xs" variant="filled" color="red">
              Block Case
            </Badge>
          )}
          {!item.preventSignOff && !item.blockCase && (
            <Text size="xs" c="dimmed">
              None
            </Text>
          )}
        </Group>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <Badge color={item.isActive ? "green" : "gray"} variant="filled">
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <div>
          <Title order={2}>Error Management</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage error types that can be reported during workflow stages
          </Text>
        </div>

        {/* Data Table */}
        <AdminDataTable
          data={errors}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          searchPlaceholder="Search errors..."
          emptyMessage="No errors configured"
        />
      </Stack>

      {/* Add/Edit Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={editingError ? "Edit Error" : "Add Error"}
        size="md"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <ErrorForm
            ref={formRef}
            initialValues={
              editingError
                ? {
                    ...editingError,
                    description: editingError.description ?? undefined,
                  }
                : undefined
            }
            onSubmit={handleSubmit}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={createError.isPending || updateError.isPending}
              onClick={() => formRef.current?.requestSubmit()}
            >
              {editingError ? "Update" : "Create"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
